import { DOCUMENT } from '@angular/common';
import { inject, Inject, Injectable, NgZone } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { AnimationController } from '@ionic/angular';
import { AccountRepository, AccountService } from '@timechef/features/account/state';
import { AnimationsRepository } from '@timechef/features/animations/state';
import { HistoryService } from '@timechef/features/history/state/history.service';
import { RestaurantsRepository } from '@timechef/features/restaurants/state';
import { combineLatest, distinctUntilChanged, filter, map, mergeMap, Observable, skip, Subject, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AppInsightsService, GoogleAnalyticsService } from '../analytics';
import { AuthRepository } from '../auth';
import { DemoProxyService } from '../demo-proxy';
import { ILogger, LoggerService } from '../logging';
import { AppRepository, AppService } from '../state';
import { RouteData } from '../utils';
import { InAppBrowserService } from './in-app-browser.service';
import { RgpdCookiesService } from './rgpd-cookies.service';

/** Handles everything that is app wide AND technical, so we don't pollute app.service */
@Injectable({ providedIn: 'root' })
export class AppCoreService {
  logger: ILogger;
  constructor(
    private authRepo: AuthRepository,
    private animationCtrl: AnimationController,
    private appService: AppService,
    private appRepo: AppRepository,
    private accountRepo: AccountRepository,
    private accountService: AccountService,
    private animationsRepo: AnimationsRepository,
    @Inject(DOCUMENT) private document: Document,
    private demoProxy: DemoProxyService,
    private googleAnalytics: GoogleAnalyticsService,
    private historyService: HistoryService,
    private inAppBrowserService: InAppBrowserService,
    private rgpdCookiesService: RgpdCookiesService,
    private route: ActivatedRoute,
    private router: Router,
    loggerService: LoggerService,
    private restaurantsRepo: RestaurantsRepository,
    private zone: NgZone,
  ) {
    this.logger = loggerService.getLogger('AppCoreService');
  }

  async init() {
    this.logger.debug('Initializing AppCoreService');

    // Refresh the cache when the language of the app change so we can fetch again data from api with the good language
    this.handleDataCacheWhenLanguageChanges();

    // App Resumed/Paused store data setup
    await this.setupAppLifecycle();

    // Demo Proxy registration
    this.registerDemoProxyChanges();

    // Handle app embed
    this.initIsAppEmbedded();

    // Deep link management (url scheme and universal link)
    this.logger.debug('Setting up deep link handler');
    await App.addListener('appUrlOpen', async (x: URLOpenListenerEvent) => {
      this.logger.info('Deep link triggered with URL: ' + x.url);
      await this.inAppBrowserService.closeCurrentBrowser();
      this.zone.run(() => {
        this.handleUrl(x.url);
      });
    });

    // When current site rules change, we check navbar items and see if some onboarding slides should be showed, it's triggered at least once when init too
    const siteRules$ = this.handleSiteRulesChange();

    this.handleUnreadsFetch(siteRules$);

    // Close the splash screen in index.html
    this.closeSplash();
  }

  appInsightsService = inject(AppInsightsService);

  willEnterHome$ = new Subject<Date>();
  willEnterHome() {
    this.willEnterHome$.next(new Date());
  }

  handleUnreadsFetch(siteRules$: Observable<{ hasSurvey: boolean }>) {
    combineLatest([
      this.authRepo.isLoggedIn$,
      siteRules$,
      this.restaurantsRepo.restaurantsRequestStatus$,
      this.appRepo.instance.initialized$,
      this.willEnterHome$,
    ])
      .pipe(
        filter(([isLoggedIn, _siteRules, req]) => isLoggedIn && req.value === 'success'),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      )
      .subscribe(([_isLoggedIn, siteRules]) => {
        this.appService.getNotificationsCount().subscribe();

        if (siteRules.hasSurvey) {
          this.appService.getUnreadChatsCount().subscribe();
        }
      });
  }

  handleSiteRulesChange() {
    const siteRulesChange = combineLatest([this.authRepo.currentSite$, this.restaurantsRepo.restaurantRules$]);
    siteRulesChange.subscribe(([currentSite, restaurantRules]) => {
      if (currentSite) {
        this.appService.computeNavbar(currentSite, restaurantRules);
        this.accountService.updateOnboarding(currentSite, restaurantRules);
      }
    });

    return siteRulesChange.pipe(
      map((x) => {
        return x[1];
      }),
    );
  }

  async setupAppLifecycle() {
    await App.addListener('appStateChange', (s) => {
      const { isActive } = s;

      const now = new Date();
      if (isActive) {
        this.appRepo.setAppResumed(now);
        this.logger.info('App resumed');
      } else {
        this.appRepo.setAppPaused(now);
        this.logger.info('App paused');
      }
    });
  }

  registerDemoProxyChanges() {
    this.appRepo.demoProxy$
      .pipe(
        tap((x) => {
          if (x) this.demoProxy.enable();
          else this.demoProxy.disable();
        }),
      )
      .subscribe();
  }

  handleDataCacheWhenLanguageChanges() {
    // We skip 1 occurence because the first one is caused by init
    this.accountRepo.language$.pipe(skip(1)).subscribe((_lang) => {
      this.animationsRepo.clearRequestCache();
      this.restaurantsRepo.clearRequestCache();
    });
  }

  handleUrl(url: string) {
    let slug = null;
    if (url.startsWith(environment.universalAppUrl)) {
      slug = url.split(environment.universalAppUrl).pop();
    } else if (url.startsWith(environment.urlScheme)) {
      slug = url.split(environment.urlScheme).pop();
    }

    if (slug) {
      this.router.navigateByUrl(slug);
    }
  }

  navigationEndRoute$ = this.router.events.pipe(
    filter((event) => event instanceof NavigationEnd),
    map(() => this.route),
    mergeMap((route) => {
      // On récupères toutes les observables data
      const datas: Observable<RouteData>[] = [route.data];

      while (route.firstChild) {
        route = route.firstChild;
        datas.push(route.data);
      }

      return combineLatest(datas);
    }),
    map((datas) => {
      const data = datas.reduce((a, b) => {
        return { ...a, ...b };
      }, {});

      return { url: this.router.url, data };
    }),
    tap((datas) => {
      this.googleAnalytics.trackPageView(datas.url, datas.data?.analyticsPageTitle);
    }),
  );

  initIsAppEmbedded() {
    this.route.queryParams.subscribe((params) => {
      if (params.embedded) {
        this.appRepo.setAppEmbedded(params.embedded);
      }
      if (params.navBarPlace) {
        this.appRepo.setNavBarPlace(params.navBarPlace);
      }
    });
  }

  closeSplash() {
    const splash = document.getElementById('splash');
    if (Capacitor.isNativePlatform()) {
      if (splash) {
        splash.remove();
      }
      SplashScreen.hide({ fadeOutDuration: 300 }); //fade out ignored on android, ios only
    } else {
      if (!splash) {
        return;
      }

      setTimeout(async () => {
        await this.animationCtrl
          .create()
          .beforeStyles({ styles: { opacity: 1 } })
          .addElement(splash.querySelector('.center')!)
          .easing('ease-out')
          .duration(500)
          .fromTo('opacity', '1', '0')
          .play();

        this.animationCtrl
          .create()
          .beforeStyles({ styles: { opacity: 1 } })
          .addElement(splash)
          .easing('ease-out')
          .duration(300)
          .fromTo('opacity', '1', '0')
          .play()
          .then();

        splash.remove();

        this.rgpdCookiesService.prompt({ isInitialPrompt: true });
      }, 750);
    }
  }
}
